import { Card, TextField } from '@mui/material';
import { Box, Container } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { RootState } from 'src/redux-store/appStore';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { httpRequest } from 'src/services/httpRequest';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import PdfGenerator from 'src/ui-component/PdfGenerator';
interface EditApplicationData {
  editApplicationFormData: {
    ApplicationType: string;
    ApplicationNumber: string;
    ApplicantName: string;
    Blocklots: string;
    CompanyName: string;
    OwnerName: string;
    ProjectContactPhone: string;
    ProjectContactName: string;
    PropertyAddress: string;
    ProjectContactAddress: string;
    DescribeUse: string;
    EIN: string;
  };
}

const NewApplication: React.FC = () => {
  const navigate = useNavigate();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const location = useLocation();
  const dispatch = useDispatch();
  const { townshipId, permit } = useParams();
  const { editApplicationFormData } =
    (location?.state as EditApplicationData) || {};

  const [planningAndZoningFormData, setPlanningAndZoningFormData] =
    useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [blockLotsOptions, setBlockLotsOptions] = useState<string[]>([]);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [clickedOn, setClickedOn] = useState<string>('');

  useEffect(() => {
    if (editApplicationFormData) {
      setPlanningAndZoningFormData({
        ...planningAndZoningFormData,
        ...editApplicationFormData
      });
    }
  }, [editApplicationFormData]);

  //Method to get application feea
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee),
        inspectionFee: Number(feeObject?.InspectionFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
  }, []);

  //Method to get block lots list
  const handleBlockLotsList = async (val) => {
    if (val) {
      if (val?.BlockLot) {
        setPlanningAndZoningFormData({
          ...planningAndZoningFormData,
          Blocklots: val?.BlockLot,
          owner: val?.Owner,
          PropertyAddress: val?.PropAddress
        });
      } else {
        if (val?.trim() === '') return;
        setPlanningAndZoningFormData({
          ...planningAndZoningFormData,
          Blocklots: val
        });
        if (val?.length > 2) {
          const response = await httpRequest(
            'GET',
            `/propertyDetails/searchBlockLots_Address?PropertyBlockLot=${val}`
          );
          if (response?.status === 'success') {
            const list = response?.data
              ?.map((value: { BlockLot: string }) => {
                return { ...value, label: value?.BlockLot };
              })
              ?.slice(0, 10);
            setBlockLotsOptions(list);
          } else if (response?.type === 'NETWORK_ERROR') {
          } else {
            toast.error(response?.message);
            dispatch(handleError(response?.message));
          }
        }
      }
    }
  };

  //Method to handle change
  const handleChange = (val, e) => {
    const { name } = e;
    if (name === 'BlockLot') {
      handleBlockLotsList(val);
    }
    else
      setPlanningAndZoningFormData({
        ...planningAndZoningFormData,
        [name]: val
      });
  };

  //Method to handle proceed to pay
  const handleProceedToPay = async (data) => {
    setLoading(true);
    const requestData = { ...planningAndZoningFormData, ...data };
    const payload = {
      ApplicantName: requestData?.ApplicantName,
      ApplicationTitle: requestData?.ApplicationTitle,
      ApplicationType: requestData?.ApplicationType,
      Blocklots: requestData?.Blocklots,
      PropertyAddress: requestData?.PropertyAddress,
      ProjectContactName: requestData?.ProjectContactName,
      ProjectContactPhone: requestData?.ProjectContactPhone,
      ProjectContactAddress: requestData?.ProjectContactAddress,
      CompanyName: requestData?.CompanyName,
      EIN: requestData?.EIN,
      DescribeUse: requestData?.DescribeUse,
      ApplicationFee: feeDetails?.applicationFee,
      InspectionFee: feeDetails?.inspectionFee
    };
    const response = await httpRequest(
      'POST',
      `/pz_application/insert`,
      payload
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: response?.data?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix:
            planningAndZoningFormData?.ApplicationType === 'Planning'
              ? 'P'
              : 'Z'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to create  NEW application
  const handleInsertApplication = async (data) => {
    setLoading(true);
    const requestData = { ...planningAndZoningFormData, ...data };
    const payload = {
      ApplicantName: requestData?.ApplicantName,
      ApplicationTitle: requestData?.ApplicationTitle,
      ApplicationType: requestData?.ApplicationType,
      Blocklots: requestData?.Blocklots,
      PropertyAddress: requestData?.PropertyAddress,
      ProjectContactName: requestData?.ProjectContactName,
      ProjectContactPhone: requestData?.ProjectContactPhone,
      ProjectContactAddress: requestData?.ProjectContactAddress,
      CompanyName: requestData?.CompanyName,
      EIN: requestData?.EIN,
      DescribeUse: requestData?.DescribeUse,
      ApplicationFee: feeDetails?.applicationFee,
      InspectionFee: feeDetails?.inspectionFee
    };
    const response = await httpRequest(
      'POST',
      `/pz_application/insert`,
      payload
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      if (clickedOn === 'Save Draft') {
        setLoading(false);
        navigate(
          `/${townshipId}/Planning Department/${permit}/draft-application`
        );
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to handle form submission
  const handleSubmit = async (data) => {
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(data);
    } else if (clickedOn === 'Save Draft') {
      handleInsertApplication(data);
    }
  };

  //Fields for form using Formik
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicationType',
      fieldLabel: 'Application Type',
      fieldType: 'select',
      required: true,
      options: ['Planning', 'Zoning'],
      initialValue: planningAndZoningFormData?.ApplicationType || '',
      dependency: handleChange
    },
    {
      fieldName: 'ApplicationTitle',
      fieldLabel: 'Application Title',
      fieldType: 'text',
      required: true,
      initialValue: planningAndZoningFormData?.ApplicationTitle || '',
      dependency: handleChange
    },
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: planningAndZoningFormData?.ApplicantName || '',
      dependency: handleChange
    },
    {
      fieldName: 'CompanyName',
      fieldLabel: 'Company Name',
      fieldType: 'text',
      required: true,
      initialValue: planningAndZoningFormData?.CompanyName || '',
      dependency: handleChange
    },
    {
      fieldName: 'ProjectContactName',
      fieldLabel: 'Project Contact Name',
      fieldType: 'text',
      required: true,
      initialValue: planningAndZoningFormData?.ProjectContactName || '',
      dependency: handleChange
    },
    {
      fieldName: 'ProjectContactPhone',
      fieldLabel: 'Project Contact Phone',
      fieldType: 'mobile',
      required: true,
      initialValue: planningAndZoningFormData?.ProjectContactPhone || '',
      dependency: handleChange
    },
    {
      fieldName: 'BlockLot',
      fieldLabel: 'Block Lot',
      options: blockLotsOptions || [
        { label: planningAndZoningFormData?.Blocklots }
      ], //blockLotsOptions?.length ? blockLotsOptions : [],
      fieldType: 'autocomplete',
      required: true,
      initialValue: planningAndZoningFormData?.Blocklots,
      dependency: handleChange
    },
    {
      fieldName: 'ProjectLocation/PropertyAddress',
      fieldLabel: 'Project Location/Property Address',
      fieldType: 'text',
      disabled: true,
      required: false,
      initialValue:
        planningAndZoningFormData?.PropertyAddress ||
        planningAndZoningFormData?.PropertyAddress ||
        '',
      dependency: handleChange
    },
    {
      fieldName: 'ProjectContactAddress',
      fieldLabel: 'Project Contact Address',
      fieldType: 'note',
      required: true,
      initialValue: planningAndZoningFormData?.ProjectContactAddress || '',
      dependency: handleChange
    },
    {
      fieldName: 'DescribeUse',
      fieldLabel: 'Describe Use',
      fieldType: 'note',
      required: true,
      initialValue: planningAndZoningFormData?.DescribeUse || '',
      dependency: handleChange
    },
    {
      fieldName: 'EIN',
      fieldLabel: 'EIN',
      fieldType: 'number',
      required: true,
      initialValue: planningAndZoningFormData?.EIN || '',
      dependency: handleChange
    }
  ];

  const PlanningAndZoningBottomSection = () => {
    return (
      <>
        <Box display={'flex'} gap={2}>
          <TextField
            id="outlined-basic"
            value={`$ ${feeDetails?.applicationFee}.00`}
            disabled
            sx={{ mt: 2 }}
            label="Application Fee *"
            variant="outlined"
            InputLabelProps={{
              className: 'weight-600'
            }}
          />
          <TextField
            id="outlined-basic"
            value={`$ ${feeDetails?.inspectionFee}.00`}
            disabled
            sx={{ mt: 2 }}
            label="Inspection Fee *"
            variant="outlined"
            InputLabelProps={{
              className: 'weight-600'
            }}
          />
        </Box>
      </>
    );
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2, pb: 4, px:{xs: 1, md: "auto"} }} className="main-table-container">
      <Breadcrumb
        PermitName={'Planning and Zoning'}
        PageTitle={'New Application'}
      />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="new"
      />
      <Card>
        <Box
          m={{ md: 3, xs: 1 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <PdfGenerator
            PermitName={'Planning and Zoning'}
            allFields={allFields}
          />
        </Box>
        <Box m={{ md: 3, xs: 1 }}>
          <FormikForm
            allFields={allFields}
            buttonTitle={['Cancel', 'Save Draft', 'Proceed to Payment']}
            fullScreen={false}
            isEditModel={false}
            content={PlanningAndZoningBottomSection}
            setClickedOn={setClickedOn}
            clickProp
            formMethod={handleSubmit}
            buttonPlacement={'right'}
            heading={'Road Permit'}
            handleOtherButton={() => {
              navigate(`/dashboard/${permit}`);
            }}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication;
